// Instance validation and normalization. A rejected configuration renders an error that names every invalid
// field instead of a table, so validation collects every error rather than stopping at the first.

import {
  DEFAULT_DENSITY,
  DEFAULT_HEADING_LEVEL,
  DEFAULT_MENU_MIN_WIDTH,
  DENSITIES,
  HEADING_LEVELS,
  PAGE_ACTION_TREATMENTS,
  SORT_DIRECTIONS,
  SOURCE_MODES,
} from './defaults'
import {
  columnName,
  isFunction,
  isPositiveInteger,
  isPositiveNumber,
  isText,
  normalizeColumn,
  validateColumn,
} from './columns'

const declared = value => value !== undefined && value !== null
const isObject = value => Boolean(value) && typeof value === 'object'

// a command runs a handler; a destination is a link
const checkTrigger = (action, name, errors) => {
  if (!isText(action.label)) errors.push(`${name}.label: required`)
  if (!isFunction(action.onSelect) && !isText(action.href))
    errors.push(`${name}: declare onSelect or href`)
}

const checkKeyedActions = (actions, field, errors, extra = () => {}) => {
  if (!Array.isArray(actions)) {
    errors.push(`${field}: must be a list of actions`)
    return
  }
  const keys = new Set()
  actions.forEach((action, i) => {
    const name = `${field}[${i}]`
    if (!isObject(action)) {
      errors.push(`${name}: missing`)
      return
    }
    if (!isText(action.key)) errors.push(`${name}.key: required`)
    else if (keys.has(action.key)) errors.push(`${name}.key: duplicate`)
    keys.add(action.key)
    checkTrigger(action, name, errors)
    if (declared(action.destructive) && typeof action.destructive !== 'boolean')
      errors.push(`${name}.destructive: must be true or false`)
    extra(action, name)
  })
}

const checkInstanceFields = (config, errors) => {
  if (declared(config.embedded) && typeof config.embedded !== 'boolean')
    errors.push('embedded: must be true or false')
  if (!isText(config.tableId)) errors.push('tableId: required')
  if (!isText(config.caption))
    errors.push('caption: an accessible table name is required')
  if (!isText(config.rowKey))
    errors.push('rowKey: the row-identity field is required')
  if (
    declared(config.headingLevel) &&
    !HEADING_LEVELS.includes(config.headingLevel)
  )
    errors.push('headingLevel: 1, 2, 3 or 4')
  if (
    declared(config.defaultDensity) &&
    !DENSITIES.includes(config.defaultDensity)
  )
    errors.push(`defaultDensity: must be ${DENSITIES.join(' or ')}`)
  ;['minTableWidth', 'minMenuWidth'].forEach(field => {
    if (declared(config[field]) && !isPositiveNumber(config[field]))
      errors.push(`${field}: must be a positive number of pixels`)
  })
  if (declared(config.rowActionsLabel) && !isText(config.rowActionsLabel))
    errors.push('rowActionsLabel: must be text')
}

const checkColumns = (config, errors) => {
  const columns = Array.isArray(config.columns) ? config.columns : []
  if (!columns.length) errors.push('columns: at least one column is required')
  const keys = new Set()
  const sourceMode = isObject(config.source) ? config.source.mode : undefined
  columns.forEach((column, i) => {
    errors.push(...validateColumn(column, i, { keys, sourceMode }))
  })
  const valid = columns.filter(isObject)
  if (!valid.some(column => column.required && column.type !== 'actions')) {
    errors.push(
      'columns: one column must be required; the first required column supplies the row identity'
    )
  }
  if (valid.filter(column => column.type === 'actions').length > 1)
    errors.push('columns: at most one actions column')
  return { columns: valid, keys }
}

const checkSort = (config, columns, keys, errors) => {
  const sort = config.defaultSort
  if (!isObject(sort) || !keys.has(sort.key)) {
    errors.push('defaultSort: must name a configured column')
  } else if (!SORT_DIRECTIONS.includes(sort.direction)) {
    errors.push(
      `defaultSort.direction: must be ${SORT_DIRECTIONS.join(' or ')}`
    )
  } else if (!columns.find(c => c.key === sort.key).sortable) {
    errors.push('defaultSort: its column must be sortable')
  }
}

const checkEmbedded = (config, errors) => {
  ;['defaultSort', 'selection', 'grouped', 'pageActions', 'summary'].forEach(
    field => {
      if (config[field] !== undefined)
        errors.push(`${field}: not allowed in embedded mode`)
    }
  )
  const columns = Array.isArray(config.columns) ? config.columns : []
  columns.forEach((column, i) => {
    if (!isObject(column)) return
    ;['sortable', 'searchable', 'filterable'].forEach(field => {
      if (column[field])
        errors.push(
          `${columnName(column, i)}: ${field} not allowed in embedded mode`
        )
    })
  })
}

const checkPaging = (config, errors) => {
  if (config.paged === false) {
    if (config.embedded !== true || (config.source || {}).mode !== 'client')
      errors.push('paged: false needs embedded mode and a client source')
    return
  }
  const sizes = config.pageSizes
  if (!Array.isArray(sizes) || !sizes.length) {
    errors.push('pageSizes: at least one page size is required')
  } else if (!sizes.every(isPositiveInteger)) {
    errors.push('pageSizes: every page size must be a positive integer')
  } else if (!sizes.includes(config.defaultPageSize)) {
    errors.push('defaultPageSize: must be one of pageSizes')
  }
}

const checkSource = (config, errors) => {
  const { source } = config
  if (!isObject(source) || !source.mode) {
    errors.push('source.mode: required')
  } else if (!SOURCE_MODES.includes(source.mode)) {
    errors.push(`source.mode: must be ${SOURCE_MODES.join(' or ')}`)
  } else if (source.mode === 'client' && !isPositiveInteger(source.max)) {
    errors.push('source.max: a client-backed source declares a finite maximum')
  }
}

const checkEmptyState = (config, errors) => {
  const empty = config.emptyState
  if (!isObject(empty) || !isText(empty.title)) {
    errors.push('emptyState.title: zero-data copy is required')
    return
  }
  if (declared(empty.action)) {
    if (!isObject(empty.action))
      errors.push('emptyState.action: must declare label and onSelect or href')
    else checkTrigger(empty.action, 'emptyState.action', errors)
  }
}

const checkSummary = (config, columns, errors) => {
  const participating = columns.some(
    column => declared(column.summary) && column.summary !== false
  )
  if (declared(config.summary)) {
    if (!isObject(config.summary) || !isText(config.summary.label))
      errors.push('summary.label: required when a summary row is configured')
    if (!participating)
      errors.push('summary: at least one column must declare its aggregate')
  } else if (participating) {
    errors.push('summary.label: a column summary needs the summary row label')
  }
}

const checkSelection = (config, errors) => {
  const { selection } = config
  if (!declared(selection) || selection === false || selection === true) return
  if (!isObject(selection)) {
    errors.push('selection: must be true or a selection configuration')
    return
  }
  if (declared(selection.bulkActions))
    checkKeyedActions(selection.bulkActions, 'selection.bulkActions', errors)
  if (declared(selection.isEligible) && !isFunction(selection.isEligible))
    errors.push('selection.isEligible: must be a function of the row')
  const reason = selection.ineligibleReason
  if (declared(reason) && !isText(reason) && !isFunction(reason))
    errors.push(
      'selection.ineligibleReason: must be text or a function of the row'
    )
}

// a value the browser accepts as a CSS color; CSS.supports where it exists, else a style probe
const isCssColor = value => {
  if (!isText(value)) return false
  if (typeof CSS !== 'undefined' && typeof CSS.supports === 'function')
    return CSS.supports('color', value)
  if (typeof document === 'undefined') return true
  const probe = document.createElement('span').style
  probe.color = value
  return probe.color !== ''
}

const checkGrouped = (config, errors) => {
  const { grouped } = config
  if (!declared(grouped) || grouped === false) return
  if (!isObject(grouped)) {
    errors.push('grouped: must declare groups and how a row finds its group')
    return
  }
  if (isObject(config.source) && config.source.mode === 'page')
    errors.push('grouped: grouped mode needs a client-backed source')
  if (!isText(grouped.key) && !isFunction(grouped.groupOf))
    errors.push('grouped: declare key (a row field) or groupOf (a function)')
  if (declared(grouped.renderHeader) && !isFunction(grouped.renderHeader))
    errors.push(
      'grouped.renderHeader: must be a function of the group and its rows'
    )
  // an instance with no groups is valid: it shows the zero-data state
  if (!Array.isArray(grouped.groups)) {
    errors.push('grouped.groups: must be a list of groups')
    return
  }
  const keys = new Set()
  grouped.groups.forEach((group, i) => {
    const name = `grouped.groups[${i}]`
    if (!isObject(group)) {
      errors.push(`${name}: missing`)
      return
    }
    if (!isText(group.key)) errors.push(`${name}.key: required`)
    else if (keys.has(group.key)) errors.push(`${name}.key: duplicate`)
    keys.add(group.key)
    if (!isText(group.title)) errors.push(`${name}.title: required`)
    if (!isText(group.emptyText) && !isText(grouped.emptyText))
      errors.push(`${name}.emptyText: empty-group copy is required`)
    if (declared(group.expanded) && typeof group.expanded !== 'boolean')
      errors.push(`${name}.expanded: must be true or false`)
    if (declared(group.color) && !isCssColor(group.color))
      errors.push(`${name}.color: must be a valid CSS color`)
  })
}

const checkPageActions = (config, errors) => {
  if (!declared(config.pageActions)) return
  checkKeyedActions(
    config.pageActions,
    'pageActions',
    errors,
    (action, name) => {
      if (
        declared(action.treatment) &&
        !PAGE_ACTION_TREATMENTS.includes(action.treatment)
      )
        errors.push(
          `${name}.treatment: must be ${PAGE_ACTION_TREATMENTS.join(' or ')}`
        )
    }
  )
}

/**
 * Every invalid field of one table configuration; empty when it is valid.
 * @returns {string[]}
 */
export const validateConfig = config => {
  if (!isObject(config)) return ['config: missing']
  const errors = []
  checkInstanceFields(config, errors)
  const { columns, keys } = checkColumns(config, errors)
  if (config.embedded === true) checkEmbedded(config, errors)
  else checkSort(config, columns, keys, errors)
  checkPaging(config, errors)
  checkSource(config, errors)
  checkEmptyState(config, errors)
  checkSummary(config, columns, errors)
  checkSelection(config, errors)
  checkGrouped(config, errors)
  checkPageActions(config, errors)
  return errors
}

export const identityColumn = config =>
  ((config && config.columns) || []).find(
    column => column && column.required && column.type !== 'actions'
  )

const noReason = () => null

const reasonOf = reason => {
  if (isFunction(reason)) return reason
  if (!isText(reason)) return noReason
  const fixed = () => reason
  return fixed
}

const normalizeSelection = selection => {
  if (!selection) return null
  const raw = isObject(selection) ? selection : {}
  return {
    bulkActions: Array.isArray(raw.bulkActions) ? raw.bulkActions : [],
    isEligible: isFunction(raw.isEligible) ? raw.isEligible : () => true,
    ineligibleReason: reasonOf(raw.ineligibleReason),
  }
}

const normalizeGrouped = grouped => {
  if (!isObject(grouped)) return null
  const field = grouped.key
  const groupOf = isFunction(grouped.groupOf)
    ? grouped.groupOf
    : row => (row && isText(field) ? row[field] : undefined)
  const groups = Array.isArray(grouped.groups)
    ? grouped.groups.filter(isObject).map(group => ({
        ...group,
        expanded: group.expanded !== false,
        emptyText: isText(group.emptyText)
          ? group.emptyText
          : grouped.emptyText,
      }))
    : []
  return { ...grouped, groupOf, groups }
}

const normalizePageActions = actions =>
  Array.isArray(actions)
    ? actions.filter(isObject).map(action => ({
        ...action,
        treatment: PAGE_ACTION_TREATMENTS.includes(action.treatment)
          ? action.treatment
          : 'secondary',
        destructive: Boolean(action.destructive),
      }))
    : []

const unpagedSize = raw =>
  isObject(raw.source) && isPositiveInteger(raw.source.max)
    ? raw.source.max
    : 10000

// The shape every module reads (types.js NormalizedConfig). It never throws: an invalid configuration still
// normalizes to a safe shape, so the shell's hooks stay well-defined while it renders the error instead.
export const normalizeConfig = config => {
  const raw = isObject(config) ? config : {}
  const minMenuWidth = isPositiveNumber(raw.minMenuWidth)
    ? raw.minMenuWidth
    : DEFAULT_MENU_MIN_WIDTH
  const columns = Array.isArray(raw.columns) ? raw.columns.filter(isObject) : []
  return {
    ...raw,
    embedded: raw.embedded === true,
    headingLevel: HEADING_LEVELS.includes(raw.headingLevel)
      ? raw.headingLevel
      : DEFAULT_HEADING_LEVEL,
    columns: columns.map(column => {
      const normalized = normalizeColumn(column, { minMenuWidth })
      return raw.embedded === true
        ? { ...normalized, resizable: false }
        : normalized
    }),
    defaultSort: isObject(raw.defaultSort)
      ? raw.defaultSort
      : { key: null, direction: 'ascending' },
    paged: raw.paged !== false,
    // an unpaged table is one page as long as the client source's ceiling
    pageSizes:
      raw.paged === false
        ? [unpagedSize(raw)]
        : Array.isArray(raw.pageSizes)
        ? raw.pageSizes
        : [],
    defaultPageSize:
      raw.paged === false
        ? unpagedSize(raw)
        : isPositiveInteger(raw.defaultPageSize)
        ? raw.defaultPageSize
        : 10,
    source: isObject(raw.source) ? raw.source : { mode: 'client' },
    emptyState: isObject(raw.emptyState) ? raw.emptyState : { title: '' },
    summary: isObject(raw.summary) ? raw.summary : null,
    selection: normalizeSelection(raw.selection),
    grouped: normalizeGrouped(raw.grouped),
    pageActions: normalizePageActions(raw.pageActions),
    minTableWidth: isPositiveNumber(raw.minTableWidth)
      ? raw.minTableWidth
      : null,
    minMenuWidth,
    defaultDensity: DENSITIES.includes(raw.defaultDensity)
      ? raw.defaultDensity
      : DEFAULT_DENSITY,
  }
}
